import { useTranslation } from 'react-i18next';
import { HpCardHeader, TableScroll } from '@hp/ui';
import type { ClusterPair as Pair, PastDecision } from './queries';
import { RuleChips, StatusBadge, TierBadge } from '../../components/badges';
import { RelativeTime } from '../../components/relative-time';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';

export function EvidencePanel({ pairs }: { pairs: Pair[] }) {
  const { t } = useTranslation(['review', 'common']);
  return (
    <section className="hp-card">
      <HpCardHeader title={t('evidence.title')} subtitle={t('evidence.subtitle')} icon="target" />
      <TableScroll label={t('evidence.title')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('evidence.pair')}</th>
            <th>{t('evidence.score')}</th>
            <th>{t('evidence.tier')}</th>
            <th>{t('evidence.rules')}</th>
            <th>{t('common:table.status')}</th>
          </BrutTheadRow>
          <tbody>
            {pairs.map((p, i) => (
              <tr key={i}>
                <td className="bo-cell-muted bo-nowrap">
                  #{p.pdIdLow} ↔ #{p.pdIdHigh}
                </td>
                <td>{p.score}</td>
                <td>
                  <TierBadge tier={p.tier} />
                </td>
                <td>
                  <RuleChips rules={p.matchedRules} />
                </td>
                <td>
                  <StatusBadge status={p.status} />
                </td>
              </tr>
            ))}
            {pairs.length === 0 && (
              <tr>
                <td colSpan={5} className="bo-cell-empty">
                  {t('evidence.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
    </section>
  );
}

export function DecisionsPanel({ decisions }: { decisions: PastDecision[] }) {
  const { t } = useTranslation(['review', 'common']);
  return (
    <section className="hp-card">
      <HpCardHeader title={t('decisions.title')} subtitle={t('decisions.subtitle')} icon="history" />
      {decisions.length === 0 ? (
        <p className="hp-card__body bo-muted">{t('decisions.empty')}</p>
      ) : (
        <ul className="bo-decision-list">
          {decisions.map((d, i) => (
            <li key={i}>
              <div className="bo-row">
                <StatusBadge
                  status={
                    d.decision === 'approve_merge' || d.decision === 'approve_delete'
                      ? 'approved'
                      : d.decision === 'not_duplicate'
                        ? 'declined'
                        : 'pending'
                  }
                />
                <strong>{t(`decisions.label.${d.decision}`, { defaultValue: d.decision })}</strong>
                {d.survivorPdId !== null && (
                  <span className="bo-mono bo-muted">{t('decisions.survivor', { id: d.survivorPdId })}</span>
                )}
                <span className="bo-muted">{d.decidedByEmail ?? t('common:misc.unknown')}</span>
                <RelativeTime value={d.decidedAt} className="bo-muted" />
              </div>
              {d.note && <p className="bo-decision-list__note">“{d.note}”</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
